<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import {
  useInsightElements,
  type PresentedInsightElement,
} from '../../composables/useInsightElements'
import type {
  InsightColumnDefinition,
  InsightElement,
  InsightTab,
} from '../../models/insight.types'
import type { InsightElementMapping } from '../../service/insightElementMapper'
import { formatStructuredInsightValue } from '../../service/insightValue'

import InsightExportCsvButton from './InsightExportCsvButton.vue'

import { ChartRenderer, ReportTable, type ReportColumn, type ReportRow } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Tabs, type TabOption, type TabValue } from '@/shared/ui/tabs'

const props = withDefaults(
  defineProps<{
    elements: readonly InsightElement[]
    tabs?: readonly InsightTab[]
    configColumns?: readonly InsightColumnDefinition[]
    emptyLabel?: string
  }>(),
  { tabs: () => [], configColumns: () => [] },
)

export interface InsightCsvRequest {
  name: string
  columns: ReportColumn[]
  rows: ReportRow[]
}

interface TableGroup {
  key: string
  label: string
  items: PresentedInsightElement[]
}

type ContentBlock =
  { kind: 'table-tabs'; key: string } | { kind: 'item'; key: string; item: PresentedInsightElement }

const emit = defineEmits<{ exportCsv: [InsightCsvRequest] }>()

const { t } = useI18n()
const format = useFormat()
const { items } = useInsightElements(
  () => props.elements,
  () => props.configColumns,
)
const activeTab = ref<TabValue>('')
const tableItems = computed(() =>
  items.value.filter((item) => item.element.element_type === 'table'),
)
const tableGroups = computed<TableGroup[]>(() => {
  const keys: string[] = []
  const addKey = (value: string | null | undefined) => {
    const key = value?.trim()
    if (key && !keys.includes(key)) keys.push(key)
  }
  props.tabs.forEach((tab) => addKey(tab.key))
  tableItems.value.forEach((item) => addKey(item.element.tab_key))

  if (keys.length === 0) {
    return tableItems.value.length === 0
      ? []
      : [{ key: 'tables', label: t('insights.elements.table'), items: [...tableItems.value] }]
  }

  return keys.map((key, index) => ({
    key,
    label:
      props.tabs.find((tab) => tab.key.trim() === key)?.label?.trim() ||
      humanizeTabKey(key) ||
      t('insights.elements.tableLabel', { index: index + 1 }),
    items: tableItems.value.filter((item) => {
      const itemKey = item.element.tab_key?.trim()
      return !itemKey || itemKey === key
    }),
  }))
})
const tabOptions = computed<TabOption[]>(() =>
  tableGroups.value.map((group) => ({ label: group.label, value: group.key })),
)
const blocks = computed<ContentBlock[]>(() => {
  if (tableItems.value.length < 2 && tableGroups.value.length < 2) {
    return items.value.map((item) => ({ kind: 'item', key: item.key, item }))
  }
  let insertedTabs = false
  const content: ContentBlock[] = []
  for (const item of items.value) {
    if (item.element.element_type !== 'table') {
      content.push({ kind: 'item', key: item.key, item })
      continue
    }
    if (insertedTabs) continue
    insertedTabs = true
    content.push({ kind: 'table-tabs', key: `table-tabs:${item.key}` })
  }
  return content
})

watch(
  tableGroups,
  (groups) => {
    if (!groups.some((group) => group.key === activeTab.value)) {
      activeTab.value = groups[0]?.key ?? ''
    }
  },
  { immediate: true },
)

/**
 * v3 labels a table tab from its own `tab_key` -- `capitalizeKeyword` in `format.js`, reached
 * through `InsightDetailModal.vue:tableElementTabs`. It never reads the config's
 * `tab_definitions[].label`, so `url_placement` shows as `Url Placement`, not `Sites and Apps`.
 */
function humanizeTabKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\p{L}/gu, (character) => character.toUpperCase())
}

function requestCsv(): void {
  const group =
    tableGroups.value.find((candidate) => candidate.key === activeTab.value) ?? tableGroups.value[0]
  const mapping = group?.items[0]?.mapping ?? tableItems.value[0]?.mapping
  if (mapping?.kind !== 'table' || mapping.rows.length === 0 || mapping.columns.length === 0) return
  emit('exportCsv', {
    name: group?.label ?? t('insights.elements.table'),
    columns: mapping.columns,
    rows: mapping.rows,
  })
}

function rawPayload(payload: unknown): string {
  return formatStructuredInsightValue(payload)
}

function totalLabel(mapping: InsightElementMapping): string {
  if (mapping.kind !== 'table' || mapping.rowCount === null) return ''
  return t('insights.elements.resultFromRows', { count: format.formatNumber(mapping.rowCount) })
}
</script>

<template>
  <div class="insight-elements grid gap-md min-w-0" data-testid="insight-element-list">
    <p
      v-if="blocks.length === 0"
      class="insight-elements__empty m-0 p-lg border border-dashed border-border rounded-md text-muted text-center"
    >
      {{ emptyLabel || t('insights.elements.empty') }}
    </p>

    <template v-for="block in blocks" :key="block.key">
      <Tabs
        v-if="block.kind === 'table-tabs' && tableGroups.length > 1"
        v-model="activeTab"
        :tabs="tabOptions"
        :aria-label="t('insights.elements.tableTabs')"
        variant="pill"
      >
        <template #actions>
          <InsightExportCsvButton @export="requestCsv" />
        </template>

        <template v-for="group in tableGroups" :key="group.key" #[`panel-${group.key}`]>
          <div class="insight-elements__stack grid gap-md min-w-0">
            <ReportTable
              v-for="item in group.items"
              :key="item.key"
              :rows="item.mapping.kind === 'table' ? item.mapping.rows : []"
              :columns="item.mapping.kind === 'table' ? item.mapping.columns : []"
              :total="item.mapping.kind === 'table' ? item.mapping.total : null"
              :total-label="totalLabel(item.mapping)"
              v-bind="{ ariaLabel: group.label }"
              max-height="30rem"
            />
          </div>
        </template>
      </Tabs>

      <div
        v-else-if="block.kind === 'table-tabs'"
        class="insight-elements__stack grid gap-md min-w-0"
        data-testid="insight-table-group"
      >
        <div class="insight-elements__actions flex justify-end">
          <InsightExportCsvButton @export="requestCsv" />
        </div>
        <ReportTable
          v-for="item in tableGroups[0]?.items ?? []"
          :key="item.key"
          :rows="item.mapping.kind === 'table' ? item.mapping.rows : []"
          :columns="item.mapping.kind === 'table' ? item.mapping.columns : []"
          :total="item.mapping.kind === 'table' ? item.mapping.total : null"
          :total-label="totalLabel(item.mapping)"
          v-bind="{ ariaLabel: tableGroups[0]?.label || t('insights.elements.table') }"
          max-height="30rem"
        />
      </div>

      <!-- `data-pdf-section` is a page-break preference for the PDF adapter, not styling: v3
           tagged every rendered element block the same way (`InsightDetailModal.vue:153`) so a
           table is not sliced across two A4 pages. -->
      <section v-else data-pdf-section class="insight-elements__item grid gap-sm min-w-0 py-sm">
        <div
          v-if="block.item.mapping.kind === 'table'"
          class="insight-elements__actions flex justify-end"
        >
          <InsightExportCsvButton @export="requestCsv" />
        </div>
        <ReportTable
          v-if="block.item.mapping.kind === 'table'"
          :rows="block.item.mapping.rows"
          :columns="block.item.mapping.columns"
          :total="block.item.mapping.total"
          :total-label="totalLabel(block.item.mapping)"
          v-bind="{ ariaLabel: t('insights.elements.table') }"
          max-height="30rem"
          :data-chart-fallback="block.item.mapping.fallbackFrom || undefined"
        />
        <ChartRenderer
          v-else-if="block.item.mapping.kind === 'chart'"
          :spec="block.item.mapping.spec"
          immutable
        />
        <div
          v-else
          class="insight-elements__raw overflow-hidden border border-border rounded-md bg-surface-sunken"
          role="group"
        >
          <span>{{ t('insights.elements.raw') }}</span>
          <pre><code>{{ rawPayload(block.item.mapping.payload) }}</code></pre>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.insight-elements .insight-elements__raw > span {
  display: block;
  padding-block: var(--ui-space-sm);
  padding-inline: var(--ui-space-md);
  border-bottom: 1px solid var(--ui-color-border);
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  font-weight: 600;
}
.insight-elements .insight-elements__raw pre {
  max-height: 20rem;
  margin: 0;
  overflow: auto;
  padding: var(--ui-space-md);
  font-family: var(--ui-font-numeric);
  font-size: 0.75rem;
  white-space: pre-wrap;
}
</style>
